import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AuthImage from "./AuthImage";
import { api, type Upload } from "../lib/api";
import { booksApi, type Book } from "../lib/books-api";
import { ordersApi } from "../lib/orders-api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const selectClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50";

/**
 * Testbestellung: schickt eine Bestellung im Shopify-Format durch denselben
 * Eingang wie der echte Webhook. Alles Weitere läuft wie bei einer echten.
 */
export function TestOrderDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[]>([]);
  const [bookId, setBookId] = useState("");
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [uniqid, setUniqid] = useState("");
  const [name, setName] = useState("");
  const [gender, setGender] = useState<"Junge" | "Mädchen">("Junge");
  const [eye, setEye] = useState("");
  const [binding, setBinding] = useState<"Softcover" | "Hardcover">("Softcover");
  const [dedication, setDedication] = useState("");
  const [matt, setMatt] = useState(false);
  const [outfitPhoto, setOutfitPhoto] = useState(false);
  const [manual, setManual] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || books.length) return;
    booksApi
      .list()
      .then((d) => {
        setBooks(d.books);
        setBookId((cur) => cur || d.books[0]?.id || "");
      })
      .catch((err) => toast.error(`Bücher laden fehlgeschlagen: ${err.message}`));
  }, [open, books.length]);

  useEffect(() => {
    if (!open || uploads.length) return;
    api
      .uploads(30, 50)
      .then((d) => setUploads(d.uploads))
      .catch((err) => toast.error(`Uploads laden fehlgeschlagen: ${err.message}`));
  }, [open, uploads.length]);

  function pickUpload(id: string) {
    setUniqid(id);
    const u = uploads.find((x) => x.uniqid === id);
    // Vorbelegen aus dem Upload; alles bleibt überschreibbar.
    setName(u?.name ?? "");
    setEye(u?.eye_color ?? "");
    const g = (u?.gender ?? "").toLowerCase();
    setGender(["f", "female", "girl", "mädchen", "weiblich"].includes(g) ? "Mädchen" : "Junge");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await ordersApi.createTestOrder({
        book_id: bookId,
        uniqid,
        child_name: name.trim() || undefined,
        gender,
        eye_color: eye.trim() || undefined,
        binding,
        dedication: dedication.trim() || undefined,
        matt,
        outfit_photo: outfitPhoto,
        manual,
        email: email.trim() || undefined,
      });
      toast.success(`Testbestellung ${r.order_number} angelegt`);
      if (!r.raw_photo_available)
        toast.warning("Originalfoto existiert nicht mehr – es wird die Vorschau als Referenz genutzt.");
      onOpenChange(false);
      navigate(`/orders/${r.order_id}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Testbestellung</DialogTitle>
            <DialogDescription>
              Läuft wie eine echte Shopify-Bestellung: Generierung, Qualitätsprüfung
              und Freigabe-Mail. In die Druckerei geht sie erst nach deiner Freigabe.
            </DialogDescription>
          </DialogHeader>

          <Field>
            <FieldLabel htmlFor="to-book">Buch</FieldLabel>
            <select
              id="to-book"
              className={selectClass}
              value={bookId}
              onChange={(e) => setBookId(e.target.value)}
              required
            >
              {books.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.title}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor="to-name">Name des Kindes</FieldLabel>
              <Input id="to-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="aus Upload" />
            </Field>
            <Field>
              <FieldLabel htmlFor="to-gender">Geschlecht</FieldLabel>
              <select
                id="to-gender"
                className={selectClass}
                value={gender}
                onChange={(e) => setGender(e.target.value as typeof gender)}
              >
                <option>Junge</option>
                <option>Mädchen</option>
              </select>
            </Field>
            <Field>
              <FieldLabel htmlFor="to-eye">Augenfarbe</FieldLabel>
              <Input id="to-eye" value={eye} onChange={(e) => setEye(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="to-binding">Einband</FieldLabel>
              <select
                id="to-binding"
                className={selectClass}
                value={binding}
                onChange={(e) => setBinding(e.target.value as typeof binding)}
              >
                <option>Softcover</option>
                <option>Hardcover</option>
              </select>
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="to-upload">Upload (Foto &amp; Vorschau)</FieldLabel>
            <select
              id="to-upload"
              className={selectClass}
              value={uniqid}
              onChange={(e) => pickUpload(e.target.value)}
              required
            >
              <option value="" disabled>
                Upload wählen …
              </option>
              {uploads.map((u) => (
                <option key={u.uniqid} value={u.uniqid}>
                  {new Date(u.generated_at).toLocaleString("de-DE", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  · {u.name ?? "ohne Namen"}
                </option>
              ))}
            </select>
            {uniqid && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <AuthImage uniqid={uniqid} kind="reference" alt="Foto" className="aspect-square w-full rounded-md object-cover" />
                <AuthImage uniqid={uniqid} kind="generated" alt="Vorschau" className="aspect-square w-full rounded-md object-cover" />
              </div>
            )}
            <FieldDescription>
              Wie bei einer echten Bestellung: Foto und Vorschau werden aus dem Upload gezogen.
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel htmlFor="to-ded">Widmung (optional)</FieldLabel>
            <Textarea id="to-ded" rows={3} value={dedication} onChange={(e) => setDedication(e.target.value)} />
          </Field>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={matt} onCheckedChange={(v) => setMatt(v === true)} />
            Matt &amp; Kratzfest Cover (Zusatzposition)
          </label>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={outfitPhoto} onCheckedChange={(v) => setOutfitPhoto(v === true)} />
            Outfit vom Foto (Zusatzposition – Bestellung startet pausiert)
          </label>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={manual}
              onCheckedChange={(v) => setManual(v === true)}
            />
            <span>
              Manuell starten
              <span className="block text-muted-foreground">
                Bestellung startet pausiert. Character-Sheet einzeln generieren,
                Szenen erst mit „Hold aufheben“.
              </span>
            </span>
          </label>

          <Field>
            <FieldLabel htmlFor="to-mail">Kunden-E-Mail (optional)</FieldLabel>
            <Input id="to-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <FieldDescription>
              Leer = test@startales.local. Mails an den Kunden (z. B. Versand) gehen an diese Adresse.
            </FieldDescription>
          </Field>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={busy || !bookId || !uniqid}>
              {busy ? "Lege an …" : "Testbestellung anlegen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
